const SPLITTER_PREFIX = "====****====DENUNCIA:";
const SPLITTER_SUFFIX = "====****====";

/**
 * Construye el separador para una denuncia específica
 * @param {string} folio - El folio del reporte
 * @param {string} classificationLabel - La etiqueta de clasificación
 * @returns {string}
 */
const buildReportSplitter = (folio, classificationLabel) => {
  return `${SPLITTER_PREFIX}${folio}|${classificationLabel}${SPLITTER_SUFFIX}`;
};

/**
 * Parsea el string de conclusión para separar la conclusión general
 * de las conclusiones por reporte
 * @param {string} conclusionString - El string completo de conclusión
 * @returns {{ generalConclusion: string, reportConclusions: Array }}
 */
export const parseConclusion = (conclusionString) => {
  if (!conclusionString || typeof conclusionString !== "string") {
    return {
      generalConclusion: "",
      reportConclusions: [],
    };
  }

  // Buscar el primer separador de denuncia
  const firstSplitterIndex = conclusionString.indexOf(SPLITTER_PREFIX);

  if (firstSplitterIndex === -1) {
    // No hay separadores, solo conclusión general
    return {
      generalConclusion: conclusionString.trim(),
      reportConclusions: [],
    };
  }

  // La conclusión general es todo antes del primer separador
  const generalConclusion = conclusionString.substring(0, firstSplitterIndex).trim();

  // Extraer las conclusiones por reporte usando regex
  const reportConclusions = [];
  const regex = /====\*\*\*\*====DENUNCIA:([^|]+)\|([^=]+)====\*\*\*\*====([\s\S]*?)(?=====\*\*\*\*====DENUNCIA:|$)/g;

  let match;
  while ((match = regex.exec(conclusionString)) !== null) {
    const folio = match[1].trim();
    const classificationLabel = match[2].trim();
    const conclusion = match[3].trim();

    reportConclusions.push({
      folio,
      classification_label: classificationLabel,
      conclusion,
    });
  }

  return {
    generalConclusion,
    reportConclusions,
  };
};

/**
 * Construye el string de conclusión combinando la conclusión general
 * con las conclusiones por reporte
 * @param {string} generalConclusion - La conclusión general
 * @param {Array} reportConclusions - Array de conclusiones por reporte
 * @returns {string}
 */
export const buildConclusionString = (generalConclusion, reportConclusions) => {
  const general = generalConclusion || "";

  // Si no hay conclusiones por reporte, solo devolver la general
  if (!reportConclusions || reportConclusions.length === 0) {
    return general;
  }

  // Filtrar conclusiones vacías
  const validConclusions = reportConclusions.filter(
    (rc) => rc.conclusion && rc.conclusion.replace(/<[^>]*>/g, "").trim() !== ""
  );

  if (validConclusions.length === 0) {
    return general;
  }

  // Construir el string con los separadores legibles
  let result = general;

  validConclusions.forEach((rc) => {
    const splitter = buildReportSplitter(rc.folio, rc.classification_label);
    result += `\n\n${splitter}\n${rc.conclusion}`;
  });

  return result;
};

/**
 * Inicializa las conclusiones por reporte basándose en assigns_details
 * Si ya existen conclusiones parseadas, las usa; si no, crea vacías
 * @param {Array} assignsDetails - Array de reportes desde investigation.assigns_details
 * @param {Array} existingConclusions - Conclusiones ya parseadas (si existen)
 * @returns {Array}
 */
export const initializeReportConclusions = (assignsDetails, existingConclusions = []) => {
  if (!assignsDetails || assignsDetails.length === 0) {
    return [];
  }

  return assignsDetails.map((report) => {
    // Buscar si ya existe una conclusión para este reporte
    const existing = existingConclusions.find(
      (ec) => ec.folio === report.folio
    );

    return {
      report_id: report.id,
      folio: report.folio,
      classification: report.classification,
      classification_label: report.classification_label,
      conclusion: existing?.conclusion || "",
    };
  });
};

/**
 * Verifica si hay conclusiones por reporte
 * @param {string} conclusionString - El string completo de conclusión
 * @returns {boolean}
 */
export const hasReportConclusions = (conclusionString) => {
  if (!conclusionString) return false;
  return conclusionString.includes(SPLITTER_PREFIX);
};

export { SPLITTER_PREFIX, SPLITTER_SUFFIX };
